import React, { useCallback, useState } from 'react';
import { Panel} from 'rsuite';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';

import RulesSidebarNav from './RulesSidebarNav';
import NavigationBar from '../Shared/NavigationBar';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import useScrollToTop from '../../helpers/useScrollToTop';
import { STACK_AND_PRIORITY } from '../../helpers/rulesData';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 24,
  headerMarginBottom: 24,
  titleMarginBottom: 8,
  sectionMarginBottom: 32,
  panelMarginBottom: 16,
  exampleMarginBottom: 12,
  examplePadding: 16,
  exampleBorderRadius: 6,
  exampleBorderLeft: 3,
  sidebarWidth: 220,
  sidebarGap: 32,
  sidebarPadding: 16,
  sidebarBorderRadius: 6,
  sidebarTitleMarginBottom: 8,
  sidebarItemPadding: '6px 0',
  stickyTop: 20,
  backLinkMarginBottom: 16,
  backLinkPadding: '10px 0',
  diagramPadding: 24,
  diagramBorderRadius: 8,
  diagramItemPadding: '10px 16px',
  diagramItemBorderRadius: 6,
  diagramItemMarginBottom: 4,
  diagramNumberSize: 28,
  diagramNumberBorderRadius: 6,
  diagramNumberMarginRight: 10,
  introPadding: 20,
  introBorderRadius: 8,
  cardLinkPadding: '1px 4px',
  cardLinkBorderRadius: 3,
  exampleLabelMarginBottom: 6,
  exampleScenarioMarginBottom: 8,
  paragraphMarginBottom: 12,
  contentLineHeight: 1.7,
  stackItemMinHeight: 48,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  subtitle: 15,
  subtitleMobile: 14,
  sidebarTitle: 14,
  sidebarTitleWeight: 600,
  sidebarTitleLetterSpacing: 1,
  sidebarItem: 14,
  sectionHeading: 20,
  sectionHeadingWeight: 600,
  content: 14,
  exampleLabel: 14,
  exampleLabelWeight: 600,
  exampleLabelLetterSpacing: 1,
  exampleScenario: 14,
  exampleScenarioWeight: 600,
  exampleResolution: 14,
  exampleResolutionLineHeight: 1.6,
  diagramItem: 14,
  diagramItemWeight: 600,
  diagramNumber: 14,
  diagramNumberWeight: 700,
  diagramArrow: 16,
  introText: 14,
  introTextLineHeight: 1.7,
  backLink: 14,
  cardLink: 14,
};


const CARD_NAME_REGEX = /\b(Lightning Bolt|Giant Growth|Counterspell|Grizzly Bears|Essence Scatter|Negate|Krosan Grip|Sudden Spoiling|Willbender|Deathrite Shaman|Forest)\b/g;

const renderTextWithCardLinks = (text, colors) => {
  const parts = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(CARD_NAME_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const cardName = match[0];
    parts.push(
      <Link
        key={`${cardName}-${match.index}`}
        to={`/cardsearch/${encodeURIComponent(cardName)}`}
        style={{
          color: colors.accentPrimary,
          backgroundColor: colors.accentBg,
          padding: SPACING.cardLinkPadding,
          borderRadius: SPACING.cardLinkBorderRadius,
          fontSize: FONT.cardLink,
          textDecoration: 'none',
        }}
      >
        {cardName}
      </Link>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};

const LIFO_STACK_ITEMS = [
  { number: 3, label: 'Negate (resolves 1st)' },
  { number: 2, label: 'Essence Scatter (resolves 2nd)' },
  { number: 1, label: 'Grizzly Bears (resolves 3rd)' },
];

const SIDEBAR_ITEMS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'stack-diagram', label: 'Stack Diagram' },
  ...STACK_AND_PRIORITY.map((section) => ({
    id: section.id,
    label: section.title,
  })),
];

const StackReference = () => {
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);

  const [activeId, setActiveId] = useState('intro');

  useScrollToTop();

  const scrollToSection = useCallback((sectionId) => {
    setActiveId(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [setActiveId]);

  const renderExamples = (examples, keyPrefix) =>
    examples.map((ex, idx) => (
      <div
        key={`${keyPrefix}-ex-${idx}`}
        style={{
          backgroundColor: t.backgroundSecondary,
          borderLeft: `${SPACING.exampleBorderLeft}px solid ${t.accentSecondary}`,
          borderRadius: SPACING.exampleBorderRadius,
          padding: SPACING.examplePadding,
          marginBottom: SPACING.exampleMarginBottom,
        }}
      >
        <div
          style={{
            fontSize: FONT.exampleLabel,
            fontWeight: FONT.exampleLabelWeight,
            color: t.accentPrimary,
            textTransform: 'uppercase',
            letterSpacing: FONT.exampleLabelLetterSpacing,
            marginBottom: SPACING.exampleLabelMarginBottom,
          }}
        >
          Example {examples.length > 1 ? idx + 1 : ''}
        </div>
        <div
          style={{
            fontSize: FONT.exampleScenario,
            fontWeight: FONT.exampleScenarioWeight,
            color: t.textPrimary,
            marginBottom: SPACING.exampleScenarioMarginBottom,
          }}
        >
          {renderTextWithCardLinks(ex.scenario, t)}
        </div>
        <div
          style={{
            fontSize: FONT.exampleResolution,
            color: t.textSecondary,
            lineHeight: FONT.exampleResolutionLineHeight,
          }}
        >
          {renderTextWithCardLinks(ex.resolution, t)}
        </div>
      </div>
    ));

  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPadding,
        }}
      >
        {/* Back link */}
        <div style={{ marginBottom: SPACING.backLinkMarginBottom }}>
          <Link to="/rules" style={{ color: t.accentPrimary, fontSize: FONT.backLink, textDecoration: 'none', display: 'inline-block', padding: SPACING.backLinkPadding }}>
            ← Back to Rules Reference
          </Link>
        </div>


        {/* Header */}
        <div style={{ marginTop: SPACING.headerMarginTop, marginBottom: SPACING.headerMarginBottom }}>
          <h1 style={{ fontSize: isMobile ? FONT.titleMobile : FONT.title, color: t.textPrimary, marginBottom: SPACING.titleMarginBottom }}>
            ⚡ Stack & Priority
          </h1>
          <div style={{ fontSize: isMobile ? FONT.subtitleMobile : FONT.subtitle, color: t.textMuted }}>
            Understanding when and how you can respond to spells and abilities
          </div>
        </div>

        {/* Layout: sidebar + content */}
        <div style={{ display: 'flex', gap: isMobile ? 0 : SPACING.sidebarGap, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Sidebar Navigation - responsive layout handled by component */}
          <RulesSidebarNav
            sections={SIDEBAR_ITEMS}
            activeId={activeId}
            onSelect={scrollToSection}
          />

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Introduction */}
            <div id="intro" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <div
                style={{
                  backgroundColor: t.backgroundSecondary,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.introBorderRadius,
                  padding: SPACING.introPadding,
                }}
              >
                <div style={{ fontSize: FONT.introText, color: t.textSecondary, lineHeight: FONT.introTextLineHeight }}>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    The <strong style={{ color: t.textPrimary }}>stack</strong> is a game zone where spells and abilities wait
                    to resolve. It is the core mechanism that enables interaction between players. Whenever a spell is cast
                    or an ability triggers, it goes on the stack, and each player has a chance to respond before it resolves.
                  </p>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    <strong style={{ color: t.textPrimary }}>Priority</strong> determines who gets to act at any given moment.
                    A player with priority can cast instants, activate abilities, or pass. When all players pass in succession,
                    the top item on the stack resolves.
                  </p>
                  <p style={{ margin: 0 }}>
                    The stack uses <strong style={{ color: t.textPrimary }}>Last In, First Out (LIFO)</strong> order.
                    The most recently added spell or ability resolves first. This means a response always resolves
                    before the thing it is responding to.
                  </p>
                </div>
              </div>
            </div>

            {/* Visual Stack Diagram */}
            <div id="stack-diagram" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Visual Stack — LIFO Order
              </h2>
              <div
                style={{
                  backgroundColor: t.cardBackground,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.diagramBorderRadius,
                  padding: SPACING.diagramPadding,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <div style={{ fontSize: FONT.exampleResolution, color: t.textMuted, marginBottom: SPACING.panelMarginBottom, textAlign: 'center' }}>
                  Player A casts {renderTextWithCardLinks('Grizzly Bears', t)}. Player B responds with {renderTextWithCardLinks('Essence Scatter', t)}. Player A responds with {renderTextWithCardLinks('Negate', t)}.
                </div>
                {LIFO_STACK_ITEMS.map((item, idx) => (
                  <React.Fragment key={item.number}>
                    <div
                      style={{
                        backgroundColor: t.accentBg,
                        border: `1px solid ${t.accentSecondary}`,
                        borderRadius: SPACING.diagramItemBorderRadius,
                        padding: SPACING.diagramItemPadding,
                        width: '100%',
                        maxWidth: 400,
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: SPACING.stackItemMinHeight,
                        marginBottom: SPACING.diagramItemMarginBottom,
                      }}
                    >
                      <div
                        style={{
                          width: SPACING.diagramNumberSize,
                          height: SPACING.diagramNumberSize,
                          borderRadius: SPACING.diagramNumberBorderRadius,
                          backgroundColor: t.accentSecondary,
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: SPACING.diagramNumberMarginRight,
                          flexShrink: 0,
                          fontSize: FONT.diagramNumber,
                          fontWeight: FONT.diagramNumberWeight,
                        }}
                      >
                        {item.number}
                      </div>
                      <span style={{ fontSize: FONT.diagramItem, fontWeight: FONT.diagramItemWeight, color: t.textPrimary }}>
                        {renderTextWithCardLinks(item.label, t)}
                      </span>
                    </div>
                    {idx < LIFO_STACK_ITEMS.length - 1 && (
                      <div style={{ fontSize: FONT.diagramArrow, color: t.textFaint, marginBottom: SPACING.diagramItemMarginBottom }}>
                        ▼
                      </div>
                    )}
                  </React.Fragment>
                ))}
                <div style={{ fontSize: FONT.exampleResolution, color: t.textMuted, marginTop: SPACING.panelMarginBottom, textAlign: 'center' }}>
                  Top of stack resolves first → {renderTextWithCardLinks('Negate', t)} counters {renderTextWithCardLinks('Essence Scatter', t)}, then {renderTextWithCardLinks('Grizzly Bears', t)} resolves.
                </div>
              </div>
            </div>

            {/* Section Panels */}
            {STACK_AND_PRIORITY.map((section) => (
              <div
                key={section.id}
                id={section.id}
                style={{ marginBottom: SPACING.sectionMarginBottom }}
              >
                <Panel
                  bordered
                  style={{ backgroundColor: t.cardBackground, borderColor: t.borderPrimary }}
                >
                  <h3 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                    {section.title}
                  </h3>
                  <div
                    style={{
                      fontSize: FONT.content,
                      color: t.textSecondary,
                      lineHeight: SPACING.contentLineHeight,
                      whiteSpace: 'pre-wrap',
                      marginBottom: section.examples && section.examples.length > 0 ? SPACING.panelMarginBottom : 0,
                    }}
                  >
                    {renderTextWithCardLinks(section.content, t)}
                  </div>
                  {section.examples && section.examples.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: FONT.exampleLabel,
                          fontWeight: FONT.exampleLabelWeight,
                          color: t.accentPrimary,
                          textTransform: 'uppercase',
                          letterSpacing: FONT.exampleLabelLetterSpacing,
                          marginBottom: SPACING.exampleLabelMarginBottom,
                        }}
                      >
                        Examples
                      </div>
                      {renderExamples(section.examples, section.id)}
                    </div>
                  )}
                </Panel>
              </div>
            ))}
          </div>
        </div>
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default StackReference;
